'use client'

import { buildDismissAssertionWrite } from '~/app/accounting/lot-assertion-writes'
import { LotTrustSignals } from '~/app/accounting/lot-trust-signals'
import { useLotAssertionWrite } from '~/app/accounting/useLotAssertionWrite'
import {
  Button,
  LabelValue,
  List,
  ListItemProps,
} from '~/components/component-library'
import { SplitsDialogContent } from '~/components/component-library/Dialog'
import { ADDRESS_ZERO } from '~/constants/addresses'
import { getChain } from '~/constants/chains'
import { Address } from '~/utils/address'
import { displayBigNumber } from '~/utils/display'

type Partition = LotTrustSignals['unmatchedAssertions'][number]
type PartitionScope = Pick<
  Partition,
  'smartAccountId' | 'chainId' | 'tokenAddress'
>

const getToken = (signals: LotTrustSignals, partition: PartitionScope) => {
  if (Address.equal(partition.tokenAddress, ADDRESS_ZERO)) {
    return getChain(partition.chainId)?.nativeCurrency
  }
  return signals.details.tokens.find(
    (token) =>
      token.chainId === partition.chainId &&
      Address.equal(token.address, partition.tokenAddress),
  )
}

const formatAmount = ({
  value,
  token,
  signed = false,
}: {
  value: string
  token: ReturnType<typeof getToken>
  signed?: boolean
}) => {
  const amount = displayBigNumber(BigInt(value), {
    displayDecimals: 'full',
    tokenDecimals: token?.decimals ?? 0,
    signDisplay: signed ? 'exceptZero' : 'auto',
  })
  const unit = token?.decimals == null ? 'base units' : token.symbol
  return unit ? `${amount} ${unit}` : amount
}

const describePartition = (
  signals: LotTrustSignals,
  partition: PartitionScope,
) => {
  const account = signals.details.accounts.find(
    (account) => account.id === partition.smartAccountId,
  )
  const token = getToken(signals, partition)
  return {
    account: account?.name || partition.smartAccountId,
    token: token?.symbol || partition.tokenAddress,
    network: getChain(partition.chainId)?.name ?? `Chain ${partition.chainId}`,
  }
}

// The row already carries names, so the panel only adds what can be copied.
const PartitionAddresses = ({
  signals,
  partition,
}: {
  signals: LotTrustSignals
  partition: PartitionScope
}) => {
  const account = signals.details.accounts.find(
    (account) => account.id === partition.smartAccountId,
  )
  const address = account?.address ?? partition.smartAccountId
  const isNative = Address.equal(partition.tokenAddress, ADDRESS_ZERO)
  return (
    <>
      <LabelValue
        size="small"
        label="Account"
        value={address}
        copyable={address}
      />
      {!isNative && (
        <LabelValue
          size="small"
          label="Token"
          value={partition.tokenAddress}
          copyable={partition.tokenAddress}
        />
      )}
    </>
  )
}

const CORRECTION_LABELS = {
  seed: 'Unused seed',
  edit: 'Unapplied lot edit',
  designate: 'Unapplied seed designation',
}

const CORRECTION_GUIDANCE = {
  seed: 'Check for duplicate seeds or missing migration transfers',
  edit: 'No matching lot. Review the original transfer and correction.',
  designate: 'No matching lot. Review the original transfer and correction.',
}

const SUPERSEDED_LABEL = 'Superseded lot edit'
const SUPERSEDED_GUIDANCE =
  'This transfer now has its real cost basis, usually from importing the account the funds came from, so the edit no longer applies to anything. Dismissing it clears this notice.'

/**
 * A superseded correction cannot be made to apply, so the only useful action is
 * to withdraw it. Inline rather than behind a confirmation: the edit is already
 * known to affect nothing, and the revoke stays in the audit history.
 */
const DismissCorrection = ({
  row,
}: {
  row: LotTrustSignals['unmatchedAssertions'][number]
}) => {
  const { writeAssertion, isProcessing } = useLotAssertionWrite()

  const dismiss = async () =>
    writeAssertion({
      assertion: buildDismissAssertionWrite(row),
      successMessage: 'Correction dismissed',
      successDescription: 'The lot keeps the cost basis it already has',
      errorMessage: 'Failed to dismiss correction',
    })

  return (
    <div>
      <Button
        variant="secondary"
        size="small"
        isLoading={isProcessing}
        onClick={dismiss}
      >
        Dismiss correction
      </Button>
    </div>
  )
}

export const LotTrustSignalsDialog = ({
  signals,
}: {
  signals: LotTrustSignals
}) => {
  const differences = (signals.reconciliation ?? []).filter(
    (row) => BigInt(row.delta) !== 0n,
  )

  const differenceItems: ListItemProps[] = differences.map((row) => {
    const token = getToken(signals, row)
    const labels = describePartition(signals, row)
    return {
      key: `${row.smartAccountId}:${row.chainId}:${row.tokenAddress}`,
      content: labels.account,
      description: `${labels.token} · ${labels.network}`,
      meta: formatAmount({ value: row.delta, token, signed: true }),
      expandableContent: (
        <div className="flex flex-col gap-2 [overflow-wrap:anywhere]">
          <LabelValue
            size="small"
            label="Open lots"
            value={formatAmount({ value: row.lotQuantity, token })}
          />
          <LabelValue
            size="small"
            label="Indexed transfers"
            value={formatAmount({ value: row.transferQuantity, token })}
          />
          <PartitionAddresses signals={signals} partition={row} />
          {token?.decimals == null && (
            <p className="text-xs text-secondary">
              Decimals unavailable, showing base units
            </p>
          )}
        </div>
      ),
    }
  })

  const correctionItems: ListItemProps[] = signals.unmatchedAssertions.map(
    (row) => {
      const labels = describePartition(signals, row)
      return {
        key: `${row.smartAccountId}:${row.chainId}:${row.tokenAddress}:${row.targetKey}`,
        content: row.superseded
          ? SUPERSEDED_LABEL
          : CORRECTION_LABELS[row.kind],
        description: `${labels.account} · ${labels.token} · ${labels.network}`,
        meta:
          row.quantity === null
            ? undefined
            : formatAmount({
                value: row.quantity,
                token: getToken(signals, row),
              }),
        expandableContent: (
          <div className="flex flex-col gap-2 [overflow-wrap:anywhere]">
            <p className="text-sm text-secondary">
              {row.superseded
                ? SUPERSEDED_GUIDANCE
                : CORRECTION_GUIDANCE[row.kind]}
            </p>
            <PartitionAddresses signals={signals} partition={row} />
            {row.superseded ? (
              <DismissCorrection row={row} />
            ) : (
              <LabelValue
                size="small"
                label="Reference"
                value={row.targetKey}
                copyable
              />
            )}
          </div>
        ),
      }
    },
  )

  return (
    <SplitsDialogContent
      id="lotTrustSignals"
      title="Accounting differences"
      variant="info"
      maxWidth="xl"
    >
      <div className="space-y-5">
        {signals.reconciliation === null ? (
          <p className="text-sm text-secondary">
            Balance checks don’t run on historical cutoffs
          </p>
        ) : (
          differenceItems.length > 0 && (
            <List
              label={`Balance differences (${differenceItems.length})`}
              description="Lots update nightly, so recent activity may explain this"
              items={differenceItems}
            />
          )
        )}
        {correctionItems.length > 0 && (
          <List
            label={`Unapplied corrections (${correctionItems.length})`}
            items={correctionItems}
          />
        )}
      </div>
    </SplitsDialogContent>
  )
}
